import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO — reusable per-page meta tags
 * Props:
 *  - title:       page <title>
 *  - description: meta description
 *  - keywords?:   comma list
 *  - image?:      og:image absolute URL
 *  - url?:        canonical URL
 *  - type?:       og:type (default 'website')
 *  - article?:    { published_at, tags[] } for blog posts
 */
export default function SEO({ title, description, keywords, image, url, type = "website", article }) {
  const fullTitle = title?.includes("Blackline") ? title : `${title} — Blackline Studio`;
  const canonical = url || (typeof window !== "undefined" ? window.location.href : undefined);
  const img = image || `${typeof window !== "undefined" ? window.location.origin : ""}/og-default.jpg`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content="Blackline Studio" />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />

      {/* Article-specific */}
      {type === "article" && article?.published_at && (
        <meta property="article:published_time" content={article.published_at} />
      )}
      {type === "article" && article?.tags?.map((t, i) => (
        <meta key={i} property="article:tag" content={t} />
      ))}

      {/* JSON-LD Organization (global) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Blackline Studio",
          url: canonical,
          logo: img,
          sameAs: ["https://instagram.com/Blackline_studio11"],
        })}
      </script>
    </Helmet>
  );
}
